import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Phone, Home, Plus, X, Upload, FileText, Loader2, Briefcase, IdCard, Trash2, Pencil, Gauge, AlertTriangle, Wand2, Power, ShieldCheck, UserPlus, Users } from "lucide-react";
import { EmptyState, openOnboarding } from "../../components/onboarding/EmptyState";
import { EMPTY as EMPTY_TXT } from "../../content/onboardingTexts";
import { LaterSection } from "../../components/onboarding/LaterSection";
import api from "../../lib/api";
import { CompanyTenantForm } from "../../components/tenants/CompanyTenantForm";
import { CompanyPieces } from "../../components/tenants/CompanyPieces";
import { CompanyDocOcr } from "../../components/tenants/CompanyDocOcr";
import { toast } from "sonner";
import OcrReviewModal from "../../components/OcrReviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const SOLVENCY_STYLE = {
  good: "bg-brand-lime/30 text-brand-greenDeep",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-red-100 text-red-600",
};

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function emailIssue(email) {
  const e = (email || "").trim();
  if (!e) return "missing";
  if (!EMAIL_RX.test(e)) return "invalid";
  return null;
}

function pieceExpiry(dateStr) {
  if (!dateStr) return null;
  const exp = new Date(dateStr);
  if (isNaN(exp)) return null;
  const days = Math.ceil((exp - new Date()) / 86400000);
  if (days < 0) return { days, label: "Pièce expirée", cls: "bg-red-100 text-red-600" };
  if (days <= 60) return { days, label: `Pièce expire dans ${days} j`, cls: "bg-amber-100 text-amber-700" };
  return null;
}

const TYPE_OPTIONS = ["Particulier", "Société / Autre"];
const CIVILITE_OPTIONS = ["Mlle", "Mme", "M.", "M. et Mme"];
const PIECE_OPTIONS = ["Carte d'identité", "Passeport", "Permis de conduire", "Carte de séjour"];
const COLORS = ["#C9A24B", "#3F5B4C", "#7C5CBF", "#C05F3C", "#3B7A9E", "#B23A6B"];

const EMPTY = {
  type_locataire: "Particulier", photo: "", color: "#C9A24B",
  civilite: "", prenom: "", prenom2: "", nom: "", date_naissance: "", lieu_naissance: "", nationalite: "",
  email: "", phone: "", profession: "", revenus_mensuels: "",
  piece_type: "", piece_numero: "", piece_expiration: "", piece_fichier: "",
  property: "", status: "À jour",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.ceil((d - new Date()) / 86400000);
}

function SolvencyCheck({ revenus, pieceExpiration }) {
  const [rent, setRent] = useState("");
  const income = Number(revenus) || 0;
  const maxRent = income ? Math.round(income / 3) : 0;
  const r = Number(rent) || 0;
  const effort = income && r ? Math.round((r / income) * 100) : null;
  const verdict = effort === null ? null : effort <= 33 ? { t: "Bon dossier", c: "text-emerald-700 bg-emerald-100" } : effort <= 40 ? { t: "Limite", c: "text-amber-700 bg-amber-100" } : { t: "Risqué", c: "text-red-600 bg-red-100" };
  const dd = daysUntil(pieceExpiration);

  if (!income && !pieceExpiration) return null;
  return (
    <div className="space-y-3" data-testid="solvency-check">
      {pieceExpiration && dd !== null && dd < 90 && (
        <div className={`flex items-start gap-2 rounded-2xl px-4 py-3 text-sm ${dd < 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`} data-testid="piece-expiry-alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{dd < 0 ? `Pièce d'identité EXPIRÉE depuis le ${new Date(pieceExpiration).toLocaleDateString("fr-FR")}. Demandez une pièce en cours de validité.` : `Pièce d'identité expire bientôt (le ${new Date(pieceExpiration).toLocaleDateString("fr-FR")}, dans ${dd} jour${dd > 1 ? "s" : ""}).`}</span>
        </div>
      )}
      {income > 0 && (
        <div className="rounded-2xl border border-brand-soft bg-brand-softer/60 p-4" data-testid="solvency-card">
          <div className="flex items-center gap-2 mb-2"><Gauge className="w-4 h-4 text-brand-greenDeep" /><span className="text-sm font-semibold text-brand-greenDeep">Solvabilité — taux d'effort</span></div>
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div><p className="text-xs text-brand-green/55">Revenus mensuels</p><p className="text-lg font-semibold text-brand-greenDeep">{income.toLocaleString("fr-FR")} €</p></div>
            <div><p className="text-xs text-brand-green/55">Loyer max recommandé (~33%)</p><p className="text-lg font-semibold text-brand-greenDeep" data-testid="max-rent">{maxRent.toLocaleString("fr-FR")} €</p></div>
            <div>
              <p className="text-xs text-brand-green/55 mb-1">Loyer envisagé (CC)</p>
              <input type="number" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="ex : 850" className="input" data-testid="envisaged-rent-input" />
            </div>
          </div>
          {effort !== null && (
            <div className="mt-3 flex items-center gap-3" data-testid="effort-result">
              <div className="flex-1 h-2.5 rounded-full bg-white overflow-hidden"><div className={`h-full rounded-full ${effort <= 33 ? "bg-emerald-500" : effort <= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.min(100, effort)}%` }} /></div>
              <span className="text-sm font-bold text-brand-greenDeep">{effort}%</span>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${verdict.c}`}>{verdict.t}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Locataires() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [piecesV, setPiecesV] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [docs, setDocs] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [accountBusy, setAccountBusy] = useState(null);
  const docRef = useRef(null);

  const TENANT_OCR_FIELDS = [
    { key: "civilite", label: "Civilité" },
    { key: "prenom", label: "Prénom" },
    { key: "prenom2", label: "2ème prénom" },
    { key: "nom", label: "Nom" },
    { key: "date_naissance", label: "Date de naissance" },
    { key: "lieu_naissance", label: "Lieu de naissance" },
    { key: "nationalite", label: "Nationalité" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Téléphone" },
    { key: "profession", label: "Profession" },
    { key: "revenus_mensuels", label: "Revenus mensuels (€)" },
    { key: "piece_type", label: "Type de pièce" },
    { key: "piece_numero", label: "N° de pièce" },
    { key: "piece_expiration", label: "Expiration pièce" },
  ];

  const addDocs = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setDocs((d) => [...d, ...files.map((f) => ({ file: f, name: f.name }))]);
    if (docRef.current) docRef.current.value = "";
  };
  const removeDoc = (i) => setDocs((d) => d.filter((_, idx) => idx !== i));
  const runOcr = async () => {
    if (!docs.length) { toast.error("Ajoutez au moins un document à analyser"); return; }
    setOcrBusy(true);
    try {
      const fd = new FormData();
      docs.forEach((d) => fd.append("files", d.file));
      const { data } = await api.post("/ai/extract-tenant-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOcrResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse OCR impossible");
    } finally { setOcrBusy(false); }
  };
  const applyOcr = (selected) => {
    setForm((f) => ({ ...f, ...selected }));
    const n = Object.keys(selected).length;
    toast.success(n ? `${n} champ(s) appliqué(s) à la fiche` : "Aucun champ sélectionné");
  };

  const [loaded, setLoaded] = useState(false);
  const load = () => api.get("/tenants").then((r) => { setItems(r.data); setLoaded(true); }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  const [errors, setErrors] = useState({});
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: false } : e)); };

  const openCreate = () => { setEditId(null); setErrors({}); setForm(EMPTY); setDocs([]); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setErrors({});
    setForm({ ...EMPTY, ...t, draft: !!t.company_draft });
    setDocs([]);
    setOpen(true);
  };

  const isCo = form.type_locataire === "Société / Autre";
  const add = async (e, force = false) => {
    e?.preventDefault?.();
    const req = isCo ? {} : {
      prenom: !form.prenom || !String(form.prenom).trim(),
      nom: !form.nom || !String(form.nom).trim(),
      email: !form.email || !EMAIL_RX.test(String(form.email).trim()),
    };
    if (Object.values(req).some(Boolean)) {
      setErrors(req);
      toast.error("Veuillez renseigner les champs obligatoires (en rouge)");
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const name = isCo ? ((form.company || {}).raison_sociale || "Société") : `${form.prenom} ${form.nom}`.trim();
      let pieceFichier = form.piece_fichier;
      if (docs.length && !pieceFichier) {
        try {
          const fu = new FormData();
          fu.append("file", docs[0].file);
          const up = await api.post("/tenants/upload-file", fu, { headers: { "Content-Type": "multipart/form-data" } });
          pieceFichier = up.data?.url || pieceFichier;
        } catch { /* pièce non attachée */ }
      }
      const payload = { ...form, name, piece_fichier: pieceFichier, draft: isCo && !!form.draft, force, email: isCo ? ((form.company || {}).contacts || {}).billing_email || "" : form.email };
      if (editId) {
        const res = await api.put(`/tenants/${editId}`, payload);
        setItems(items.map((t) => (t.id === editId ? res.data : t)));
        toast.success("Locataire mis à jour");
      } else {
        const res = await api.post("/tenants", payload);
        setItems([res.data, ...items]);
        toast.success("Locataire ajouté");
      }
      setOpen(false);
      setForm(EMPTY);
      setDocs([]);
      setEditId(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "warnings") {
        setSaving(false);
        if (window.confirm(`Attention :\n- ${d.warnings.join("\n- ")}\n\nEnregistrer quand même ?`)) add(null, true);
        return;
      }
      if (d?.code === "duplicate") {
        const ex = items.find((x) => x.id === d.tenant_id);
        if (window.confirm(`${d.message}.\nOuvrir la fiche existante ?`) && ex) openEdit(ex);
      } else if (d?.code === "invalid") toast.error(`À corriger : ${d.errors.join(" · ")}`, { duration: 8000 });
      else toast.error(typeof d === "string" ? d : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce locataire ?")) return;
    try {
      await api.delete(`/tenants/${id}`);
      setItems(items.filter((t) => t.id !== id));
      toast.success("Locataire supprimé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };

  const createAccount = async (t) => {
    const eIssue = emailIssue(t.email);
    if (eIssue) { toast.error("Renseignez d'abord une adresse email valide pour ce locataire."); return; }
    if (!window.confirm(`Créer / activer un compte locataire pour ${t.name} (${t.email}) ?\n\nLe locataire pourra se connecter avec cette adresse (via Google) et accédera directement à son espace, sans validation de l'administrateur.`)) return;
    setAccountBusy(t.id);
    try {
      const res = await api.post(`/tenants/${t.id}/create-account`);
      setItems((p) => p.map((x) => (x.id === t.id ? { ...x, has_account: true } : x)));
      if (res.data?.warning) toast(res.data.warning);
      else if (res.data?.existed) toast.success("Compte déjà existant — accès locataire activé");
      else toast.success("Compte locataire créé et validé automatiquement");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création du compte impossible");
    } finally { setAccountBusy(null); }
  };

  const toggleActive = async (t) => {
    const next = t.active === false; // if currently inactive → activate
    try {
      await api.patch(`/tenants/${t.id}/active`, { active: next });
      setItems((p) => p.map((x) => (x.id === t.id ? { ...x, active: next } : x)));
      toast.success(next ? "Locataire réactivé" : "Locataire rendu inactif");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };

  const isActive = (t) => t.active !== false;
  const visible = items.filter((t) => showInactive || isActive(t));
  const inactiveCount = items.filter((t) => !isActive(t)).length;

  const emptyTenants = loaded && items.length === 0 && !open && (properties.length
    ? <EmptyState icon={Users} title={EMPTY_TXT.locataires.title.replace("{bien}", properties[0].name)} cta={EMPTY_TXT.locataires.cta} onCta={openCreate} testid="empty-locataires" />
    : <EmptyState icon={Users} title={EMPTY_TXT.locatairesNoProp.title} cta={EMPTY_TXT.locatairesNoProp.cta} onCta={() => openOnboarding(1)} testid="empty-locataires-noprop" />);
  if (emptyTenants) return emptyTenants;
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-brand-green/60">{visible.length} locataire{visible.length > 1 ? "s" : ""}{!showInactive && inactiveCount > 0 ? ` · ${inactiveCount} inactif(s) masqué(s)` : ""}</p>
        <div className="flex items-center gap-2">
          <div className="flex p-1 bg-brand-softer rounded-full" data-testid="tenant-active-toggle">
            <button onClick={() => setShowInactive(false)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!showInactive ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="tenant-filter-active">Actifs</button>
            <button onClick={() => setShowInactive(true)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${showInactive ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="tenant-filter-all">Tous</button>
          </div>
          <button onClick={openCreate} data-testid="add-tenant-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {visible.map((t) => {
          const pe = pieceExpiry(t.piece_expiration);
          const inactive = t.active === false;
          const eIssue = emailIssue(t.email);
          return (
          <div key={t.id} onClick={() => navigate(`/app/locataires/${t.id}`)} className={`bg-white rounded-2xl p-6 border hover:shadow-lg transition-all group cursor-pointer ${inactive ? "border-brand-soft opacity-60" : "border-brand-soft"}`} data-testid={`tenant-card-${t.id}`}>
            <div className="flex items-center gap-4">
              {t.photo ? (
                <img src={imgSrc(t.photo)} alt={t.name} className="w-14 h-14 rounded-2xl object-cover" />
              ) : (
                <span className="w-14 h-14 rounded-2xl flex items-center justify-center font-semibold text-lg text-white" style={{ backgroundColor: t.color || "#C9A24B" }}>{t.avatar}</span>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg text-brand-greenDeep truncate">{t.name}</h3>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {inactive
                    ? <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-brand-soft text-brand-green/60" data-testid={`tenant-inactive-badge-${t.id}`}>Inactif</span>
                    : <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${t.status === "À jour" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-red-100 text-red-600"}`}>{t.status}</span>}
                  {t.type_locataire && <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-brand-softer text-brand-green/70">{t.type_locataire}</span>}{t.company_draft && <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700" data-testid={`tenant-draft-${t.id}`}>Brouillon</span>}
                  {t.has_account && <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 bg-emerald-100 text-emerald-700" data-testid={`account-active-badge-${t.id}`}><ShieldCheck className="w-3 h-3" /> Compte actif</span>}
                  {eIssue && <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 bg-red-100 text-red-600" data-testid={`email-alert-${t.id}`}><AlertTriangle className="w-3 h-3" /> {eIssue === "missing" ? "Email manquant" : "Email invalide"}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => toggleActive(t)} title={inactive ? "Réactiver" : "Rendre inactif"} className={`${inactive ? "text-brand-limeDark hover:text-brand-greenDeep" : "text-brand-green/40 hover:text-amber-600"}`} data-testid={`toggle-active-tenant-${t.id}`}><Power className="w-4 h-4" /></button>
                <button onClick={() => openEdit(t)} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid={`edit-tenant-${t.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(t.id)} className="text-brand-green/40 hover:text-red-500" data-testid={`delete-tenant-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {(t.solvency || pe) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-3">
                {t.solvency && (
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${SOLVENCY_STYLE[t.solvency.level]}`} data-testid={`solvency-badge-${t.id}`}>
                    <Gauge className="w-3 h-3" /> Solvabilité {t.solvency.label} · {t.solvency.ratio}%
                  </span>
                )}
                {pe && <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${pe.cls}`} data-testid={`piece-alert-${t.id}`}><AlertTriangle className="w-3 h-3" /> {pe.label}</span>}
              </div>
            )}
            <div className="mt-4 space-y-2.5 text-sm text-brand-green/70">
              <p className={`flex items-center gap-2.5 ${eIssue ? "text-red-600 font-medium" : ""}`} data-testid={`tenant-email-${t.id}`}><Mail className={`w-4 h-4 ${eIssue ? "text-red-500" : "text-brand-green/40"}`} /> {t.email || "Aucune adresse email"}{eIssue === "invalid" ? " (format invalide)" : ""}</p>
              <p className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-brand-green/40" /> {t.phone || "—"}</p>
              <p className="flex items-center gap-2.5"><Home className="w-4 h-4 text-brand-green/40" /> {t.property || "—"}</p>
              {t.profession && <p className="flex items-center gap-2.5"><Briefcase className="w-4 h-4 text-brand-green/40" /> {t.profession}{t.revenus_mensuels ? ` · ${t.revenus_mensuels} €/mois` : ""}</p>}
              {t.piece_type && (
                <p className="flex items-center gap-2.5"><IdCard className="w-4 h-4 text-brand-green/40" /> {t.piece_type}{t.piece_numero ? ` · ${t.piece_numero}` : ""}
                  {t.piece_fichier && <a href={imgSrc(t.piece_fichier)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-brand-limeDark hover:underline ml-1" data-testid={`tenant-piece-link-${t.id}`}>voir</a>}
                </p>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-brand-soft flex items-center justify-between gap-2">
              <span className="text-xs text-brand-green/50">Locataire depuis le {t.since ? new Date(t.since).toLocaleDateString("fr-FR") : "—"}</span>
              {!t.has_account && !eIssue && (
                <button
                  onClick={(e) => { e.stopPropagation(); createAccount(t); }}
                  disabled={accountBusy === t.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-gold/50 text-brand-limeDark hover:bg-brand-gold/10 text-xs font-semibold transition-colors disabled:opacity-60 shrink-0"
                  data-testid={`create-account-btn-${t.id}`}
                >
                  {accountBusy === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  Créer le compte
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tenant-modal">
            <div className="flex items-center justify-between p-6 sticky top-0 bg-white border-b border-brand-soft z-10">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">{editId ? "Modifier le locataire" : "Nouveau locataire"}</h3>
              <button onClick={() => setOpen(false)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={add} className="p-6 space-y-8">
              {/* Documents & OCR */}
              <Section title="Documents & pré-remplissage (OCR)">
                <p className="text-xs text-brand-green/50 -mt-2">Ajoutez la pièce d'identité et/ou les justificatifs (fiche de paie, avis d'imposition…). L'IA lit ces documents et propose de pré-remplir la fiche. Une pièce jointe est enregistrée comme copie de la pièce d'identité.</p>
                <div className="space-y-2">
                  {docs.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 bg-brand-softer rounded-xl px-3 py-2" data-testid={`tenant-doc-${i}`}>
                      <FileText className="w-4 h-4 text-brand-greenDeep shrink-0" />
                      <span className="text-sm text-brand-greenDeep truncate flex-1">{d.name}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="text-brand-green/40 hover:text-red-500" data-testid={`tenant-doc-remove-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => docRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="add-tenant-doc-btn"><Upload className="w-4 h-4" /> Ajouter un document</button>
                  <button type="button" onClick={runOcr} disabled={ocrBusy || !docs.length} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-50" data-testid="ocr-prefill-tenant-btn">{ocrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Analyser (OCR) et pré-remplir</button>
                  <input ref={docRef} type="file" accept=".pdf,image/*" multiple className="hidden" onChange={addDocs} data-testid="tenant-doc-input" />
                </div>
              </Section>

              {!isCo && <SolvencyCheck revenus={form.revenus_mensuels} pieceExpiration={form.piece_expiration} />}

              {/* Type de locataire */}
              <Section title="Type de locataire">
                <div className="flex gap-3">
                  {TYPE_OPTIONS.map((opt) => (
                    <button key={opt} type="button" onClick={() => set("type_locataire", opt)} data-testid={`type-${opt}`}
                      className={`flex-1 py-2.5 rounded-xl border font-medium text-sm transition-colors ${form.type_locataire === opt ? "bg-brand-lime border-brand-limeDark text-brand-greenDeep" : "border-brand-soft text-brand-green/70 hover:bg-brand-softer"}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </Section>

              {isCo && <CompanyDocOcr value={form.company} onChange={(c) => set("company", c)} tid={editId} onPieceAdded={() => setPiecesV((v) => v + 1)} />}
              {isCo && <CompanyTenantForm value={form.company} onChange={(c) => set("company", c)} />}
              {isCo && editId && <CompanyPieces key={piecesV} tid={editId} />}
              {isCo && <label className="flex items-center gap-2 text-sm text-brand-greenDeep"><input type="checkbox" checked={!!form.draft} onChange={(e) => set("draft", e.target.checked)} className="accent-brand-greenDeep" data-testid="co-draft" /> Enregistrer en brouillon (SIRET + raison sociale suffisent)</label>}

              {/* Photo et couleur */}
              <Section later title="Photo et couleur">
                <div className="flex items-center gap-4">
                  <PhotoUpload value={form.photo} color={form.color} onChange={(url) => set("photo", url)} />
                  <div>
                    <p className="text-sm text-brand-green/70 mb-2">Couleur de la vignette</p>
                    <div className="flex gap-2">
                      {COLORS.map((c) => (
                        <button key={c} type="button" onClick={() => set("color", c)} data-testid={`color-${c}`}
                          className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-brand-greenDeep scale-110" : ""}`} style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  </div>
                </div>
              </Section>

              {!isCo && <>
              {/* Informations personnelles */}
              <Section title="Informations personnelles">
                <Field label="Civilité">
                  <select value={form.civilite} onChange={(e) => set("civilite", e.target.value)} className="input" data-testid="civilite-select">
                    <option value="">—</option>
                    {CIVILITE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Prénom *" error={errors.prenom}><input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} className="input" data-testid="prenom-input" /></Field>
                  <Field label="2ème prénom"><input value={form.prenom2} onChange={(e) => set("prenom2", e.target.value)} className="input" data-testid="prenom2-input" /></Field>
                </div>
                <Field label="Nom *" error={errors.nom}><input value={form.nom} onChange={(e) => set("nom", e.target.value)} className="input" data-testid="nom-input" /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Date de naissance"><input type="date" value={form.date_naissance} onChange={(e) => set("date_naissance", e.target.value)} className="input" data-testid="naissance-date-input" /></Field>
                  <Field label="Lieu de naissance"><input value={form.lieu_naissance} onChange={(e) => set("lieu_naissance", e.target.value)} className="input" data-testid="lieu-naissance-input" /></Field>
                </div>
                <Field label="Nationalité"><input value={form.nationalite} onChange={(e) => set("nationalite", e.target.value)} className="input" data-testid="nationalite-input" /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email *" error={errors.email}><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input" data-testid="email-input" /></Field>
                  <Field label="Téléphone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input" data-testid="phone-input" /></Field>
                </div>
              </Section>

              {/* Situation professionnelle */}
              <Section later title="Situation professionnelle">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Profession"><input value={form.profession} onChange={(e) => set("profession", e.target.value)} className="input" data-testid="profession-input" /></Field>
                  <Field label="Revenus mensuels (€)"><input type="number" value={form.revenus_mensuels} onChange={(e) => set("revenus_mensuels", e.target.value)} className="input" data-testid="revenus-input" /></Field>
                </div>
              </Section>

              {/* Pièce d'identité */}
              <Section later title="Pièce d'identité">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Type">
                    <select value={form.piece_type} onChange={(e) => set("piece_type", e.target.value)} className="input" data-testid="piece-type-select">
                      <option value="">—</option>
                      {PIECE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Numéro"><input value={form.piece_numero} onChange={(e) => set("piece_numero", e.target.value)} className="input" data-testid="piece-numero-input" /></Field>
                </div>
                <Field label="Date d'expiration"><input type="date" value={form.piece_expiration} onChange={(e) => set("piece_expiration", e.target.value)} className="input" data-testid="piece-expiration-input" /></Field>
                <Field label="Fichier (copie de la pièce)">
                  <FileUpload value={form.piece_fichier} onChange={(url) => set("piece_fichier", url)} />
                </Field>
              </Section>

              </>}

              {/* Bien associé */}
              <Section later title="Logement">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Bien associé">
                    <select value={form.property} onChange={(e) => set("property", e.target.value)} className="input" data-testid="property-select">
                      <option value="">— Aucun —</option>
                      {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Statut">
                    <select value={form.status} onChange={(e) => set("status", e.target.value)} className="input" data-testid="status-select">
                      <option value="À jour">À jour</option>
                      <option value="En retard">En retard</option>
                    </select>
                  </Field>
                </div>
              </Section>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-green font-semibold">Annuler</button>
                <button type="submit" disabled={saving} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60" data-testid="save-tenant-btn">{saving ? "..." : "Enregistrer"}</button>
              </div>
              {editId && (() => {
                const cur = items.find((x) => x.id === editId);
                const acc = cur?.has_account;
                return (
                  <div className="rounded-2xl border border-brand-soft bg-brand-softer p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-brand-greenDeep flex items-center gap-1.5">
                        <ShieldCheck className={`w-4 h-4 ${acc ? "text-emerald-600" : "text-brand-green/40"}`} />
                        Accès à l'application
                      </p>
                      <p className="text-xs text-brand-green/60 mt-0.5">{acc ? "Ce locataire possède un compte validé et reçoit ses messages directement dans l'application." : "Créez un compte « locataire » validé automatiquement. Le locataire se connectera avec son adresse email (via Google) et accédera à son espace, sans validation admin."}</p>
                    </div>
                    {acc
                      ? <span className="shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold bg-emerald-100 text-emerald-700" data-testid="modal-account-active">Compte actif</span>
                      : <button type="button" onClick={() => cur && createAccount(cur)} disabled={accountBusy === editId} className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-gold hover:bg-brand-limeDark text-white text-xs font-semibold transition-colors disabled:opacity-60" data-testid="modal-create-account-btn">{accountBusy === editId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Créer le compte locataire</button>}
                  </div>
                );
              })()}
            </form>
          </div>
        </div>
      )}
      <OcrReviewModal
        open={!!ocrResult}
        onClose={() => setOcrResult(null)}
        detected={ocrResult?.fields || {}}
        current={form}
        fields={TENANT_OCR_FIELDS}
        confidence={ocrResult?.confidence}
        summary={ocrResult?.summary}
        onApply={applyOcr}
      />

      <style>{`.input{width:100%;padding:0.7rem 1rem;border-radius:1rem;border:1px solid #efe7d6;outline:none;transition:all .2s;background:#fff}.input:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.25)}.field-error .input{border-color:#ef4444!important;box-shadow:0 0 0 3px rgba(239,68,68,.15)}`}</style>
    </div>
  );
}

function Section({ title, children, later }) {
  if (later) return <LaterSection title={title}>{children}</LaterSection>;
  return (
    <div>
      <h4 className="font-semibold text-brand-greenDeep border-b border-brand-soft pb-2 mb-4">{title}</h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children, error }) {
  return (
    <label className={`block ${error ? "field-error" : ""}`}>
      <span className={`block text-sm font-medium mb-1.5 ${error ? "text-red-600" : "text-brand-green/70"}`}>{label}</span>
      {children}
      {error && <span className="block text-xs text-red-600 mt-1">Ce champ obligatoire est requis</span>}
    </label>
  );
}

function PhotoUpload({ value, color, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/tenants/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec de l'upload"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="photo-upload-input" />
      <button type="button" onClick={() => ref.current?.click()} className="relative w-20 h-20 rounded-2xl border-2 border-dashed border-brand-soft flex items-center justify-center overflow-hidden hover:border-brand-limeDark transition-colors" data-testid="photo-upload-btn" style={value ? {} : { backgroundColor: color + "22" }}>
        {value ? <img src={imgSrc(value)} alt="photo" className="w-full h-full object-cover" /> : (busy ? <Loader2 className="w-5 h-5 animate-spin text-brand-green/50" /> : <Upload className="w-5 h-5 text-brand-green/40" />)}
      </button>
    </div>
  );
}

function FileUpload({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/tenants/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Fichier ajouté");
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec de l'upload"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="piece-upload-input" />
      <button type="button" onClick={() => ref.current?.click()} className="w-full py-3 rounded-xl border border-dashed border-brand-soft flex items-center justify-center gap-2 text-sm text-brand-green/60 hover:border-brand-limeDark transition-colors" data-testid="piece-upload-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : value ? <FileText className="w-4 h-4 text-brand-limeDark" /> : <Upload className="w-4 h-4" />}
        {value ? "Fichier ajouté — remplacer" : "Ajouter une copie (image ou PDF)"}
      </button>
    </div>
  );
}
